import { Component, type ErrorInfo, type ReactNode } from 'react';
import './ErrorBoundary.css';

interface Props {
  children: ReactNode;
  /** Short name of the region, shown in the fallback (e.g. "Timeline"). */
  label?: string;
  /**
   * Fallback renderer. Defaults to a compact card with a "try again" reset.
   * `reset` clears the caught error and re-renders children.
   */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Contains render/lifecycle crashes so one broken panel doesn't blank the whole
 * app. Wrap the top-level <App/> and each editor panel independently. React has
 * no hook equivalent, so this is a class component.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface for debugging; a real app might ship this to an error service.
    console.error(`[ErrorBoundary${this.props.label ? ` ${this.props.label}` : ''}]`, error, info);
  }

  reset = (): void => this.setState({ error: null });

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);
    return (
      <div className="error-boundary" role="alert">
        <p className="error-boundary-title">
          {this.props.label ? `${this.props.label} hit an error` : 'Something went wrong'}
        </p>
        <p className="error-boundary-msg">{error.message}</p>
        <div className="error-boundary-actions">
          <button className="ghost" onClick={this.reset}>
            Try again
          </button>
          <button className="ghost" onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
      </div>
    );
  }
}
